import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import JitsiFrame from '../components/VideoCallFrame';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Ticket, Users, Calendar, Lock, Loader2, Sparkles, ShoppingCart, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PublicEvent — unauthenticated event ticket page. Shows title, cover,
 * counter of spots left, and the buy form. For free events you join
 * immediately; for paid events we hand back a PayPal checkout link and
 * the access code they'll need to enter the live stream once paid.
 *
 * `?access=XXXX` auto-loads the live viewer with upsell sidebar. In-
 * stream merchandise is simply the admin-selected POS products shown
 * beside the Jitsi frame — clicking an upsell opens the public POS
 * product page (or whatever external link the admin set).
 */
export default function PublicEvent() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const [ev, setEv] = useState(null);
  const [buyer, setBuyer] = useState({ buyer_name: '', buyer_email: '' });
  const [purchase, setPurchase] = useState(null); // { access_code, status, pay_link }
  const [submitting, setSubmitting] = useState(false);
  const [stream, setStream] = useState(null);     // { url, slug, display_name } when live
  const accessCode = search.get('access') || purchase?.access_code;

  useEffect(() => {
    axios.get(`${API}/public/events/${slug}`).then(r => setEv(r.data)).catch(() => setEv(null));
  }, [slug]);

  useEffect(() => {
    if (!accessCode) return;
    axios.get(`${API}/public/events/${slug}/stream`, { params: { access_code: accessCode } })
      .then(r => setStream(r.data))
      .catch(() => {});
  }, [slug, accessCode]);

  const buyTicket = async (e) => {
    e.preventDefault();
    if (!buyer.buyer_name.trim() || !buyer.buyer_email.trim()) { toast.error('Name + email required'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/events/${slug}/tickets`, { event_id: ev.id, ...buyer });
      setPurchase(data);
      if (data.status === 'PAID') toast.success('You’re in! Your access link is ready.');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Ticket purchase failed'); }
    finally { setSubmitting(false); }
  };

  if (!ev) return <div className='p-10 text-center text-slate-500'>Loading event…</div>;

  const left = (ev.max_tickets || 0) - (ev.tickets_sold || 0);
  const isLive = !!stream;

  return (
    <div className='min-h-screen bg-gradient-to-b from-[#0B1220] via-[#0B1220] to-[#1B3A87] text-white'>
      <div className='max-w-6xl mx-auto px-4 py-10'>
        {/* Header */}
        <div className='mb-6 text-center'>
          <Badge className='bg-[#C8102E] text-white mb-3'><Sparkles className='w-3 h-3 mr-1' /> Live Event</Badge>
          <h1 className='font-display text-3xl sm:text-5xl font-bold'>{ev.title}</h1>
          <p className='text-slate-300 mt-2 max-w-2xl mx-auto'>{ev.description}</p>
          <div className='flex items-center justify-center gap-4 mt-4 text-sm text-slate-300 flex-wrap'>
            <span><Calendar className='w-4 h-4 inline mr-1' /> {new Date(ev.starts_at).toLocaleString()}</span>
            <span><Users className='w-4 h-4 inline mr-1' /> {ev.duration_min} min</span>
            <span className='font-bold text-white'><Ticket className='w-4 h-4 inline mr-1' /> {left} spots left</span>
          </div>
        </div>

        {isLive ? (
          <div className='grid lg:grid-cols-[1fr_300px] gap-4'>
            <div className='aspect-video rounded-xl overflow-hidden'>
              <JitsiFrame roomSlug={stream.slug} displayName={stream.display_name} subject={stream.event_title} />
            </div>
            {ev.upsells?.length > 0 && (
              <Card className='p-4 bg-white/10 border-white/20 text-white backdrop-blur-sm' data-testid='event-upsells'>
                <h3 className='font-display font-bold mb-3 flex items-center gap-2'><ShoppingCart className='w-4 h-4' /> Shop the stream</h3>
                <div className='space-y-3'>
                  {ev.upsells.map(p => (
                    <a key={p.id} href={`/pricing#product-${p.id}`} className='flex items-center gap-3 p-2 rounded hover:bg-white/10 transition-colors' data-testid={`upsell-${p.id}`}>
                      {(p.image_base64 || p.image_url) && <img src={p.image_base64 || p.image_url} alt='' className='w-12 h-12 rounded object-cover bg-white/5' />}
                      <div className='flex-1 min-w-0'>
                        <div className='font-semibold truncate'>{p.name}</div>
                        <div className='text-xs text-slate-300'>${(p.price || 0).toFixed(2)}</div>
                      </div>
                      <ExternalLink className='w-3.5 h-3.5 opacity-60' />
                    </a>
                  ))}
                </div>
              </Card>
            )}
          </div>
        ) : purchase ? (
          <Card className='p-6 bg-white text-slate-900 max-w-lg mx-auto' data-testid='ticket-confirm-card'>
            <h2 className='font-display text-xl font-bold mb-3'>Your ticket</h2>
            <p className='text-sm text-slate-600 mb-3'>Save this access code — you&rsquo;ll need it to join when we go live.</p>
            <div className='font-mono text-lg font-bold bg-slate-50 border rounded p-3 mb-4 text-center' data-testid='access-code'>{purchase.access_code}</div>
            {purchase.status !== 'PAID' && purchase.pay_link && (
              <a href={purchase.pay_link} target='_blank' rel='noopener noreferrer' className='block w-full text-center py-3 rounded-full bg-[#C8102E] text-white font-bold hover:bg-[#A50D26]' data-testid='event-paypal-link'>
                <Lock className='w-4 h-4 inline mr-2' /> Pay ${ev.price} with PayPal
              </a>
            )}
            {purchase.status === 'PAID' && (
              <a href={`?access=${purchase.access_code}`} className='block w-full text-center py-3 rounded-full bg-[#1B3A87] text-white font-bold hover:bg-[#16306F]'>Enter the live stream &rarr;</a>
            )}
          </Card>
        ) : (
          <Card className='p-6 bg-white text-slate-900 max-w-lg mx-auto' data-testid='ticket-buy-card'>
            <h2 className='font-display text-xl font-bold mb-3'>Get your ticket</h2>
            {left <= 0 ? (
              <div className='text-center text-[#C8102E] font-bold py-6'>Sold out</div>
            ) : (
              <form onSubmit={buyTicket} className='space-y-3'>
                <div><Label>Name</Label><Input required value={buyer.buyer_name} onChange={e => setBuyer({ ...buyer, buyer_name: e.target.value })} data-testid='buyer-name' /></div>
                <div><Label>Email</Label><Input type='email' required value={buyer.buyer_email} onChange={e => setBuyer({ ...buyer, buyer_email: e.target.value })} data-testid='buyer-email' /></div>
                <Button type='submit' disabled={submitting} className='w-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold py-3' data-testid='buy-ticket-btn'>
                  {submitting ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : null}
                  {ev.price > 0 ? `Reserve ticket — $${ev.price}` : 'Get free ticket'}
                </Button>
              </form>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
